import { useCallback, useMemo } from 'react'
import { useLocation } from 'wouter'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'

import OracleLogo from '@/assets/integrations/OracleLogo.webp'
import MewsLogo from '@/assets/integrations/MewsLogo.webp'
import ApaleoLogo from '@/assets/integrations/ApaleoLogo.webp'
import StayNTouchLogo from '@/assets/integrations/StayNTouchLogo.webp'
import TacLogo from '@/assets/integrations/TacLogo.webp'

import { useFeatures } from '../hooks/useFeatures'

const logos = {
  [GraphQL.Providers.Oracle]: OracleLogo,
  [GraphQL.Providers.Mews]: MewsLogo,
  [GraphQL.Providers.Apaleo]: ApaleoLogo,
  [GraphQL.Providers.Stayntouch]: StayNTouchLogo,
  [GraphQL.Providers.Tac]: TacLogo
}

const featureMapping: Record<GraphQL.IntegrationTypes, GraphQL.Providers[]> = {
  [GraphQL.IntegrationTypes.Pms]: [
    GraphQL.Providers.Oracle,
    GraphQL.Providers.Mews,
    GraphQL.Providers.Apaleo,
    GraphQL.Providers.Stayntouch
  ],
  [GraphQL.IntegrationTypes.BookingEngine]: [
    GraphQL.Providers.Mews,
    GraphQL.Providers.Tac
  ]
}

function Provider({
  name,
  isInstalled,
  onClick
}: {
  name: GraphQL.Providers
  isInstalled: boolean
  onClick: () => void
}) {
  return (
    <button
      className={clsx(
        'relative border rounded p-3 flex flex-col gap-2.5 items-center justify-end cursor-pointer aspect-square w-52',
        isInstalled ? 'border-primary' : 'border-text-light'
      )}
      onClick={onClick}
      tabIndex={0}
    >
      <div className="absolute inset-0 size-full p-5">
        <img className="size-full object-contain" src={logos[name]} />
      </div>
      <h3 className="text-heading3 text-text">{name}</h3>
      {isInstalled && (
        <span className="absolute top-2 right-3 text-heading3 text-primary">
          <Trans>Installed</Trans>
        </span>
      )}
    </button>
  )
}

export function FeatureSelector() {
  const { t } = useLingui()
  const [, navigate] = useLocation()

  const features = useFeatures()

  const typeLabels = useMemo(() => {
    return {
      [GraphQL.IntegrationTypes.Pms]: t`PMSs and POSs`,
      [GraphQL.IntegrationTypes.BookingEngine]: t`Booking Engines`
    } as Record<GraphQL.IntegrationTypes, string>
  }, [t])

  const connectedFeatures = useMemo(() => {
    return new Set(
      features?.filter(f => f.isSetupDone).map(f => `${f.type}_${f.provider}`)
    )
  }, [features])

  const onSelect = useCallback(
    (type: GraphQL.IntegrationTypes, provider: GraphQL.Providers) => {
      navigate(`/integration/${provider}/${type}`)
    },
    [navigate]
  )

  const sortedFeatures = useMemo(() => {
    return Object.entries(featureMapping).map(function ([type, providers]) {
      const sortedProviders = providers.toSorted((pA, pB) => {
        const isConnectA = connectedFeatures.has(`${type}_${pA}`)
        const isConnectB = connectedFeatures.has(`${type}_${pB}`)

        if (isConnectA && !isConnectB) return -1
        if (!isConnectA && isConnectB) return 1
        return 0
      })

      return [type, sortedProviders]
    }) as Array<[GraphQL.IntegrationTypes, GraphQL.Providers[]]>
  }, [connectedFeatures])

  return (
    <div className="flex flex-col gap-20 p-2.5">
      {sortedFeatures.map(function ([type, providers]) {
        const castType = type as GraphQL.IntegrationTypes

        return (
          <div className="flex flex-col w-full" key={type}>
            <h2 className="text-heading2 text-text w-full flex flex-row items-center gap-2.5 px-2.5">
              <div className="flex-1 bg-text-light h-[2px]" />
              {typeLabels[castType]}
              <div className="flex-1 bg-text-light h-[2px]" />
            </h2>
            <div className={'flex flex-row flex-wrap gap-4 p-4'}>
              {providers.map(provider => (
                <Provider
                  key={provider}
                  name={provider}
                  isInstalled={connectedFeatures.has(`${type}_${provider}`)}
                  onClick={() => onSelect(castType, provider)}
                />
              ))}
            </div>
          </div>
        )
      })}
      <p className="text-regular text-text">
        <Trans>
          If the wanted integration/provider is missing, please write us an
          email to{' '}
          <a
            className="text-info-500 underline"
            href="mailto:help@guestgoodie.com"
          >
            help@guestgoodie.com
          </a>
          . If the Provider supports integrations, we can set one up for you.
        </Trans>
      </p>
    </div>
  )
}
